import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, Project, API_BASE } from "../App";
import { supabase, authedFetch } from "../supabaseClient";
import CategorizationView from "./CategorizationView";

// ── Types ─────────────────────────────────────────────────────────────────────

type UploadStatus = "idle" | "uploading" | "analyzing" | "success" | "error";

interface FileState {
  file: File | null;
  status: UploadStatus;
  result: any | null;
  error: string | null;
}

const EMPTY_FILE_STATE: FileState = {
  file: null, status: "idle", result: null, error: null,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

// Display canonical YYYY-MM as M/YYYY (no leading zero on month), matching
// the app-wide convention used in CategorizationView and BenchmarkingView.
// Defensive: returns the raw string on malformed input rather than throwing.
// Empty / null passes through as empty so the First Revenue Period input
// renders blank when unset.
function formatPeriod(s: string | null | undefined): string {
  if (!s) return '';
  const [y, m] = s.split('-');
  if (!y || !m) return s;
  const mNum = parseInt(m, 10);
  if (isNaN(mNum)) return s;
  return `${mNum}/${y}`;
}

// ── Drop Zone ─────────────────────────────────────────────────────────────────

function DropZone({
  label,
  description,
  templateHref,
  templateName,
  state,
  onFile,
  onClear,
  disabled,
}: {
  label: string;
  description: string;
  templateHref: string;
  templateName: string;
  state: FileState;
  onFile: (f: File) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const f = e.dataTransfer.files[0];
      if (f) onFile(f);
    },
    [onFile, disabled]
  );

  const icon = { idle: "⬆", success: "✓", error: "✕" }[
    state.status as "idle" | "success" | "error"
  ];

  const borderColor = {
    idle:      dragging ? "border-emerald-500" : "border-gray-700",
    uploading: "border-blue-500",
    analyzing: "border-blue-500",
    success:   "border-emerald-500",
    error:     "border-red-500",
  }[state.status];

  const iconColor = {
    idle:      dragging ? "text-emerald-400" : "text-gray-600",
    uploading: "text-blue-400",
    analyzing: "text-blue-400",
    success:   "text-emerald-400",
    error:     "text-red-400",
  }[state.status];

  // Clean SVG spinner for uploading/analyzing states
  const isSpinning = state.status === "uploading" || state.status === "analyzing";
  const spinSpeed = state.status === "analyzing" ? "3s" : "1s";

  const SpinnerIcon = () => (
    <svg
      className="text-blue-400"
      style={{ animation: `spin ${spinSpeed} linear infinite` }}
      width="30" height="30" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );

  return (
    <div className={`flex flex-col gap-3 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</span>
        <a
          href={templateHref}
          download={templateName}
          className="text-xs text-emerald-500 hover:text-emerald-400 transition-colors"
        >
          ↓ Download template
        </a>
      </div>

      <div
        className={`relative border-2 border-dashed rounded-lg transition-all cursor-pointer ${borderColor} ${
          state.status === "idle" && !disabled ? "hover:border-gray-500 hover:bg-gray-800/30" : ""
        }`}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && state.status === "idle" && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        />

        <div className="flex flex-col items-center justify-center py-10 px-6 text-center gap-3">
          {isSpinning ? (
            <SpinnerIcon />
          ) : (
            <span className={`text-3xl leading-none ${iconColor}`}>{icon}</span>
          )}

          {state.status === "idle" && (
            <>
              <p className="text-gray-400 text-sm">Drop CSV or Excel file here, or click to browse</p>
              <p className="text-gray-600 text-xs">{description}</p>
            </>
          )}
          {state.status === "uploading" && (
            <p className="text-blue-400 text-sm">Uploading {state.file?.name}…</p>
          )}
          {state.status === "analyzing" && (
            <div className="space-y-1">
              <p className="text-blue-400 text-sm">Analyzing your data…</p>
              <p className="text-blue-400/60 text-xs">Classifying line items into MetricFrame's format</p>
            </div>
          )}
          {state.status === "success" && state.result && (
            <div className="space-y-1">
              <p className="text-emerald-400 text-sm font-bold">{state.file?.name}</p>
              <p className="text-gray-400 text-xs">
                {state.result.line_items ?? state.result.rows_inserted} line items · {state.result.periods} periods
              </p>
              <p className="text-gray-500 text-xs">
                {state.result.first_period} → {state.result.last_period}
              </p>
              {state.result.normalization_info && (
                <p className="text-blue-400/70 text-xs">
                  AI classified {state.result.normalization_info.rows_classified} line items
                  {state.result.normalization_info.rows_skipped > 0 &&
                    ` · ${state.result.normalization_info.rows_skipped} skipped`}
                </p>
              )}
              {state.result.parse_info && state.result.parse_info.skipped_columns?.length > 0 && (
                <p className="text-gray-500 text-xs">
                  Columns skipped: {state.result.parse_info.skipped_columns.join(', ')}
                </p>
              )}
            </div>
          )}
          {state.status === "error" && (
            <div className="space-y-1">
              <p className="text-red-400 text-sm font-bold">Upload failed</p>
              <p className="text-red-500/80 text-xs max-w-xs">{state.error}</p>
            </div>
          )}
        </div>

        {(state.status === "success" || state.status === "error") && (
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-300 text-xs transition-colors"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ── Project Metadata Box ─────────────────────────────────────────────────────

function MetadataBox({
  project,
  onSaved,
}: {
  project: Project;
  onSaved: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  // Dropdown options from backend
  const [stageOptions, setStageOptions]         = useState<string[]>([]);
  const [businessModelOptions, setBusinessModelOptions] = useState<string[]>([]);

  // Local editable state — initialized from project
  const [stage, setStage]                       = useState(project.stage ?? '');
  const [sector, setSector]                     = useState(project.sector ?? '');
  const [businessModel, setBusinessModel]       = useState(project.business_model ?? '');
  const [firstRevPeriod, setFirstRevPeriod]     = useState(formatPeriod(project.first_revenue_period));
  const [pic, setPic]                           = useState(project.pic ?? '');
  const [includeInBenchmarks, setIncludeInBenchmarks] = useState(project.include_in_benchmarks ?? false);
  // Error banner for the First Revenue Period field — shown when the backend
  // rejects the user's input (422 from normalize_period). Cleared on next
  // successful save. Scoped to this one field because it's the only metadata
  // input that goes through server-side parsing.
  const [firstRevPeriodError, setFirstRevPeriodError] = useState<string | null>(null);

  // Sync local state when project changes (e.g., switching projects)
  useEffect(() => {
    setStage(project.stage ?? '');
    setSector(project.sector ?? '');
    setBusinessModel(project.business_model ?? '');
    setFirstRevPeriod(formatPeriod(project.first_revenue_period));
    setPic(project.pic ?? '');
    setIncludeInBenchmarks(project.include_in_benchmarks ?? false);
    setFirstRevPeriodError(null);
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch dropdown options
  useEffect(() => {
    authedFetch(`${API_BASE}/api/benchmarks/options`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setStageOptions(d.stage_options ?? []);
          setBusinessModelOptions(d.business_model_options ?? []);
        }
      })
      .catch(() => {});
  }, []);

  // Save helper — PATCHes only the provided fields.
  // Returns { ok, body? } so callers that need to react to the backend's
  // canonical response (e.g. First Revenue Period after normalization) can.
  // Callers that don't care (dropdowns, checkboxes) can ignore the return.
  const save = async (fields: Record<string, any>): Promise<{ ok: boolean; body?: any }> => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await authedFetch(`${API_BASE}/api/benchmarks/metadata?project_id=${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const body = await res.json().catch(() => null);
      if (res.ok) {
        setSaved(true);
        onSaved();
        setTimeout(() => setSaved(false), 1500);
      }
      setSaving(false);
      return { ok: res.ok, body };
    } catch {
      setSaving(false);
      return { ok: false };
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (collapsed) {
    return (
      <div
        className="border border-gray-800 rounded-lg px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-800/30 transition-colors"
        onClick={() => setCollapsed(false)}
      >
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-xs select-none">▶</span>
          <span className="text-xs font-bold text-gray-400">Project Metadata for Benchmarking</span>
          {includeInBenchmarks && (
            <span className="text-[10px] text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded">Benchmarking</span>
          )}
        </div>
        <span className="text-gray-600 text-xs">{project.name}</span>
      </div>
    );
  }

  return (
    <div className="border border-gray-800 rounded-lg">
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-800/30 transition-colors border-b border-gray-800"
        onClick={() => setCollapsed(true)}
      >
        <span className="text-gray-500 text-xs select-none">▼</span>
        <span className="text-xs font-bold text-gray-400">Project Metadata</span>
        {saving && <span className="text-gray-600 text-[10px] ml-auto">Saving...</span>}
        {saved && <span className="text-emerald-500 text-[10px] ml-auto">Saved</span>}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Auto fields */}
        <div className="flex gap-6 text-xs">
          <div>
            <span className="text-gray-600">Project</span>
            <p className="text-gray-300">{project.name}</p>
          </div>
          <div>
            <span className="text-gray-600">Created</span>
            <p className="text-gray-300">{formatDate(project.created_at)}</p>
          </div>
        </div>

        {/* Editable fields — compact grid */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-600 text-[10px] block mb-1">Stage</label>
            <select
              value={stage}
              onChange={e => { setStage(e.target.value); save({ stage: e.target.value }); }}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500"
            >
              <option value="">—</option>
              {stageOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-600 text-[10px] block mb-1">Business Model</label>
            <select
              value={businessModel}
              onChange={e => { setBusinessModel(e.target.value); save({ business_model: e.target.value }); }}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500"
            >
              <option value="">—</option>
              {businessModelOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-600 text-[10px] block mb-1">Sector</label>
            <input
              type="text"
              value={sector}
              onChange={e => setSector(e.target.value)}
              onBlur={() => save({ sector })}
              placeholder="e.g. Healthcare, Fintech"
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-gray-600 text-[10px] block mb-1">First Revenue Period</label>
            <input
              type="text"
              value={firstRevPeriod}
              onChange={e => {
                setFirstRevPeriod(e.target.value);
                // Clear the error as soon as the user starts typing again —
                // the old error no longer describes the current input.
                if (firstRevPeriodError) setFirstRevPeriodError(null);
              }}
              onBlur={async () => {
                // Skip save if value unchanged from project's current canonical
                // (comparing display-form to display-form). Avoids a PATCH on
                // every blur, which would re-normalize an already-canonical
                // value harmlessly but noisily.
                if (firstRevPeriod === formatPeriod(project.first_revenue_period)) return;
                const { ok, body } = await save({ first_revenue_period: firstRevPeriod });
                if (ok) {
                  // Backend returns canonical YYYY-MM; display as M/YYYY to
                  // match the app-wide convention (e.g. "Jan 2024" → stored
                  // as "2024-01" → shown as "1/2024"). DB and analytics
                  // continue to read canonical; only the input's display
                  // layer is formatted.
                  const canonical = body?.updated?.first_revenue_period;
                  if (typeof canonical === 'string') setFirstRevPeriod(formatPeriod(canonical));
                  setFirstRevPeriodError(null);
                } else {
                  setFirstRevPeriodError(body?.detail ?? 'Could not parse as a date.');
                }
              }}
              placeholder="M/YYYY, Jan 2024, Q1 2024, …"
              className={`w-full bg-gray-800 border rounded px-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none ${
                firstRevPeriodError
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-700 focus:border-blue-500'
              }`}
            />
            {firstRevPeriodError && (
              <p className="text-red-400 text-[10px] mt-1">{firstRevPeriodError}</p>
            )}
          </div>
          <div>
            <label className="text-gray-600 text-[10px] block mb-1">PIC</label>
            <input
              type="text"
              value={pic}
              onChange={e => setPic(e.target.value)}
              onBlur={() => save({ pic })}
              placeholder="Paid-in capital"
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Benchmark toggle */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => {
              const next = !includeInBenchmarks;
              setIncludeInBenchmarks(next);
              save({ include_in_benchmarks: next });
            }}
            className={`w-8 h-4 rounded-full transition-colors relative ${includeInBenchmarks ? 'bg-emerald-600' : 'bg-gray-700'}`}
          >
            <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${includeInBenchmarks ? 'left-4' : 'left-0.5'}`} />
          </button>
          <span className="text-xs text-gray-400">Include in benchmarking library</span>
        </div>
      </div>
    </div>
  );
}

// ── Upload View ───────────────────────────────────────────────────────────────

export default function UploadView({
  projectId,
  isAuthenticated,
}: {
  projectId: string;
  isAuthenticated: boolean;
}) {
  const { userId, activeProject, refreshProjects, setActiveProject, deleteProject } = useAuth();

  // Project setup state
  const [projectName, setProjectName]         = useState('');
  const [projectReady, setProjectReady]       = useState(false);
  const [projectError, setProjectError]       = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectMode, setNewProjectMode]   = useState(false); // user clicked "+ New Project"

  // Delete project state
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteError, setDeleteError]             = useState('');
  const [deleting, setDeleting]                   = useState(false);

  // Existing data flags — checked from Supabase on load, not just session state
  const [hasExistingFinancials, setHasExistingFinancials] = useState(false);
  const [hasExistingCustomers, setHasExistingCustomers]   = useState(false);

  // Replace warning state
  const [showReplaceWarning, setShowReplaceWarning] = useState(false);
  const [pendingFile, setPendingFile]               = useState<{ file: File; endpoint: string; setState: any } | null>(null);

  // File upload state
  const [financials, setFinancials] = useState<FileState>(EMPTY_FILE_STATE);
  const [customers, setCustomers]   = useState<FileState>(EMPTY_FILE_STATE);

  // Incremented on every successful upload — passed to CategorizationView as a
  // prop so its internal fetchData effect re-runs and the review tables show
  // the newly uploaded data without requiring a tab switch or page refresh.
  const [refreshKey, setRefreshKey] = useState(0);

  // The project to upload to — either existing active project or newly created one
  const [uploadProjectId, setUploadProjectId] = useState<string | null>(
    activeProject ? activeProject.id : null
  );

  // ── Check if project already has data ────────────────────────────────────
  // Determines whether to show replace warning on fresh drop (cross-session)

  useEffect(() => {
    const pid = uploadProjectId ?? activeProject?.id;
    if (!pid) return;

    supabase
      .from('financials')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', pid)
      .then(({ count }) => setHasExistingFinancials((count ?? 0) > 0));

    supabase
      .from('customer_revenue')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', pid)
      .then(({ count }) => setHasExistingCustomers((count ?? 0) > 0));
  }, [uploadProjectId, activeProject?.id]);

  // ── Project creation ──────────────────────────────────────────────────────

  const handleCreateProject = async () => {
    if (!projectName.trim()) { setProjectError('Please enter a project name'); return; }
    setProjectError('');
    setCreatingProject(true);

    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id:  userId,
        name:     projectName.trim(),
        is_demo:  false,
      })
      .select()
      .single();

    setCreatingProject(false);

    if (error) {
      setProjectError(error.message);
      return;
    }

    await refreshProjects(undefined, true);
    setActiveProject(data);
    setUploadProjectId(data.id);
    setHasExistingFinancials(false);
    setHasExistingCustomers(false);
    setNewProjectMode(false);
    setProjectReady(true);
  };

  // ── File upload ───────────────────────────────────────────────────────────

  const doUpload = async (
    file: File,
    endpoint: string,
    setState: React.Dispatch<React.SetStateAction<FileState>>,
    pid: string
  ) => {
    setState({ file, status: "uploading", result: null, error: null });

    // For non-instant uploads (AI path), show "analyzing" after 2s
    const analyzingTimer = setTimeout(() => {
      setState(prev => prev.status === "uploading" ? { ...prev, status: "analyzing" } : prev);
    }, 2000);

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await authedFetch(`${API_BASE}${endpoint}?project_id=${pid}`, {
        method: "POST",
        body: form,
      });
      clearTimeout(analyzingTimer);
      const data = await res.json();
      if (!res.ok) {
        setState({ file, status: "error", result: null, error: data.detail ?? "Unknown error" });
      } else {
        setState({ file, status: "success", result: data, error: null });
        // Keep existing data flags in sync for subsequent drops in this session
        if (endpoint.includes('financials')) setHasExistingFinancials(true);
        else setHasExistingCustomers(true);
        // Signal CategorizationView to refetch so the review tables reflect
        // the new upload immediately (no tab-switch or refresh required).
        setRefreshKey(k => k + 1);
      }
    } catch {
      clearTimeout(analyzingTimer);
      setState({ file, status: "error", result: null, error: "Could not reach backend — is it running?" });
    }
  };

  const handleFile = (
    file: File,
    endpoint: string,
    setState: React.Dispatch<React.SetStateAction<FileState>>
  ) => {
    const pid = uploadProjectId!;
    const isFinancials = endpoint.includes('financials');
    const currentState = isFinancials ? financials : customers;
    const hasExisting  = isFinancials ? hasExistingFinancials : hasExistingCustomers;

    // Warn if replacing — either this session or from a previous session
    if (currentState.status === 'success' || hasExisting) {
      setPendingFile({ file, endpoint, setState });
      setShowReplaceWarning(true);
      return;
    }

    doUpload(file, endpoint, setState, pid);
  };

  const confirmReplace = () => {
    if (!pendingFile || !uploadProjectId) return;
    setShowReplaceWarning(false);
    doUpload(pendingFile.file, pendingFile.endpoint, pendingFile.setState, uploadProjectId);
    setPendingFile(null);
  };

  const handleDeleteProject = async () => {
    if (!uploadProjectId) return;
    setDeleteError('');
    setDeleting(true);
    const err = await deleteProject(uploadProjectId);
    setDeleting(false);
    if (err) {
      setDeleteError(err);
    } else {
      setShowDeleteWarning(false);
      setUploadProjectId(null);
      setFinancials(EMPTY_FILE_STATE);
      setCustomers(EMPTY_FILE_STATE);
      setProjectReady(false);
    }
  };

  // ── Unauthenticated gate ──────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-5">
        <h2 className="text-white text-lg font-semibold">Sign in to upload your data</h2>
        <p className="text-gray-500 text-sm max-w-md mx-auto">
          Create a free account to upload your financials and customer data and run the full
          MetricFrame analysis. You're currently viewing sample data.
        </p>
        <p className="text-gray-600 text-xs">
          Use the <span className="text-emerald-400">Sign up</span> link in the sidebar to get started.
        </p>
      </div>
    );
  }

  // ── Project setup prompt ──────────────────────────────────────────────────

  const showProjectSetup = (!projectReady && !activeProject) || newProjectMode;

  if (showProjectSetup) {
    return (
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div>
          <h2 className="text-white text-base font-bold tracking-wide">
            {newProjectMode ? 'New Project' : 'Name Your Project'}
          </h2>
          <p className="text-gray-500 text-xs mt-1">
            Give this dataset a name before uploading. This helps you identify it later —
            for example "Acme Corp Dec 2024" or "Portfolio Co Q3 2025".
          </p>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            placeholder="e.g. Acme Corp Dec 2024"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
            className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            autoFocus
          />
          {projectError && <p className="text-red-400 text-xs">{projectError}</p>}
          <div className="flex gap-3">
            <button
              onClick={handleCreateProject}
              disabled={creatingProject || !projectName.trim()}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm px-6 py-2.5 rounded transition-colors font-medium"
            >
              {creatingProject ? 'Creating...' : 'Create Project & Continue'}
            </button>
            {newProjectMode && (
              <button
                onClick={() => { setNewProjectMode(false); setProjectName(''); setProjectError(''); }}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-6 py-2.5 rounded transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Upload UI ─────────────────────────────────────────────────────────────

  const currentProjectName = activeProject?.name ?? projectName;

  return (
    <div className="py-8 space-y-8">
      <div className="max-w-5xl space-y-8">

      {/* Replace warning modal */}
      {showReplaceWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full space-y-4">
            <h3 className="text-white text-sm font-semibold">Replace existing data?</h3>
            <p className="text-gray-400 text-xs">
              Uploading will replace matching financial statements in <span className="text-white font-medium">{currentProjectName}</span>.
              Other statements will be preserved. Customer data is always fully replaced.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmReplace}
                className="flex-1 bg-red-700 hover:bg-red-600 text-white text-xs py-2 rounded transition-colors"
              >
                Replace Data
              </button>
              <button
                onClick={() => { setShowReplaceWarning(false); setPendingFile(null); }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-2 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete project modal */}
      {showDeleteWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full space-y-4">
            <h3 className="text-white text-sm font-semibold">Delete project?</h3>
            <p className="text-gray-400 text-xs">
              This will permanently delete <span className="text-white font-medium">{currentProjectName}</span> and all
              its financial and customer data. This cannot be undone.
            </p>
            {deleteError && <p className="text-red-400 text-xs">{deleteError}</p>}
            <div className="flex gap-3">
              <button
                onClick={handleDeleteProject}
                disabled={deleting}
                className="flex-1 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-xs py-2 rounded transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
              <button
                onClick={() => { setShowDeleteWarning(false); setDeleteError(''); }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-2 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <h2 className="text-white text-base font-bold tracking-wide">Upload Data</h2>
            <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded">
              {currentProjectName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setNewProjectMode(true); setProjectName(''); setProjectError(''); setFinancials(EMPTY_FILE_STATE); setCustomers(EMPTY_FILE_STATE); }}
              className="text-xs text-gray-500 hover:text-white border border-gray-700 hover:border-gray-500 px-3 py-1.5 rounded transition-colors"
            >
              + New Project
            </button>
            <button
              onClick={() => { setShowDeleteWarning(true); setDeleteError(''); }}
              className="text-xs text-red-600 hover:text-red-400 border border-red-900/40 hover:border-red-700 px-3 py-1.5 rounded transition-colors"
            >
              Delete Project
            </button>
          </div>
        </div>
        <p className="text-gray-500 text-xs">
          Upload financials and customer revenue data. Both files are required to run the full analysis.
          You can use the MetricFrame template or upload any CSV/Excel export — AI will classify your data automatically.
        </p>
      </div>

      {/* Drop zones */}
      <div className="space-y-6">
        <DropZone
          label="Financials"
          description="P&L, balance sheet, cash flow, and volume metrics (GMV/TPV/AUM) — CSV or Excel"
          templateHref={`${API_BASE}/api/templates/financials`}
          templateName="metricframe_financials_template.csv"
          state={financials}
          onFile={(f) => handleFile(f, '/api/upload/financials', setFinancials)}
          onClear={() => setFinancials(EMPTY_FILE_STATE)}
        />
        <DropZone
          label="Customer Revenue"
          description="Revenue by customer by period — CSV or Excel"
          templateHref={`${API_BASE}/api/templates/customers`}
          templateName="metricframe_customer_revenue_template.csv"
          state={customers}
          onFile={(f) => handleFile(f, '/api/upload/customers', setCustomers)}
          onClear={() => setCustomers(EMPTY_FILE_STATE)}
        />

      </div>

      {/* Format notes */}
      <div className="border border-gray-800 rounded-lg p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Format notes</p>
        <div className="space-y-2 text-xs text-gray-500">
          <div className="flex gap-2">
            <span className="text-emerald-500 flex-shrink-0">✓</span>
            <span>
              <span className="text-gray-300">Accepted formats:</span>{' '}
              CSV (<code className="text-gray-400">.csv</code>), Excel (<code className="text-gray-400">.xlsx</code>, <code className="text-gray-400">.xls</code>).
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-emerald-500 flex-shrink-0">✓</span>
            <span>
              <span className="text-gray-300">Financials:</span>{' '}
              use the MetricFrame template for instant processing, or upload any P&L / balance sheet / cash flow / volume metrics export and AI will classify your line items automatically.
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-emerald-500 flex-shrink-0">✓</span>
            <span>
              <span className="text-gray-300">Customer Revenue:</span>{' '}
              use the template, or upload any file with customer rows and period columns. Common column names are recognised automatically.
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-emerald-500 flex-shrink-0">✓</span>
            <span>
              <span className="text-gray-300">Periods:</span> monthly or quarterly — cadence detected automatically.
              Accepted formats: <code className="text-gray-400">YYYY-MM</code>, <code className="text-gray-400">MM/YYYY</code>, <code className="text-gray-400">Jan-2024</code>, <code className="text-gray-400">Jan-24</code>, <code className="text-gray-400">Q1 2024</code>, <code className="text-gray-400">1Q24</code>, and more.
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-emerald-500 flex-shrink-0">✓</span>
            <span>
              <span className="text-gray-300">Sign handling:</span>{' '}
              costs and expenses can be entered as positive or negative — MetricFrame normalizes signs automatically.
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">○</span>
            <span className="text-gray-400">
              After uploading, review and edit categorizations below. AI-classified rows are flagged for easy review.
            </span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">○</span>
            <span className="text-gray-400">Financials: uploading replaces matching statements only — other statements are preserved. Customer data is always fully replaced.</span>
          </div>
        </div>
      </div>

      </div>

      {/* ── Review Categorization ── */}
      <div className="border-t border-gray-800 pt-8 px-0">
        <div className="mb-8">
          <h2 className="text-white text-base font-bold tracking-wide">Review Source Data</h2>
          <p className="text-gray-500 text-xs mt-1">
            Review and edit categorizations for your uploaded data. Changes flow through to all analyses automatically.
          </p>
        </div>

        {/* Project Metadata */}
        {activeProject && (
          <MetadataBox
            project={activeProject}
            onSaved={() => refreshProjects(undefined, true)}
          />
        )}

        <CategorizationView projectId={projectId} refreshKey={refreshKey} />
      </div>

    </div>
  );
}
